// ─────────────────────────────────────────────
//  Onboarding — 4-step merchant signup flow
// ─────────────────────────────────────────────
import { useState } from "react";
import MiniQR from "../components/MiniQR";

const STEPS = ["onboard-1", "onboard-2", "onboard-3", "onboard-4"];

const PLATFORMS_LIST = [
  { id:"google",      name:"Google Reviews", icon:"G", color:"#4285F4", note:"Most impactful for local search" },
  { id:"yelp",        name:"Yelp",           icon:"Y", color:"#D32323", note:"Key for restaurants & services" },
  { id:"tripadvisor", name:"TripAdvisor",    icon:"T", color:"#00AF87", note:"Great for hospitality & tourism" },
  { id:"facebook",    name:"Facebook",       icon:"f", color:"#1877F2", note:"Reaches your existing followers" },
];

const BIZ_TYPES = ["🍽️ Restaurant","☕ Café","🍕 Takeaway","💇 Salon","🛍️ Retail","🏨 Hotel","🍺 Bar","🌮 Food Truck"];

// Shared shell with progress bar and continue button
function Shell({ step, children, nextLabel="Continue →", canNext=true, onNext }) {
  const stepIndex = STEPS.indexOf(step);
  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0f", fontFamily:"'Plus Jakarta Sans', sans-serif", color:"#f0ede8", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 }}>
      {/* Logo */}
      <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:32 }}>
        <div style={{ width:30, height:30, borderRadius:9, background:"linear-gradient(135deg,#FF6B35,#FFBE0B)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>⚡</div>
        <span style={{ fontWeight:900, fontSize:16, letterSpacing:"-0.04em" }}>JiffyReview</span>
      </div>

      {/* Progress */}
      <div style={{ display:"flex", gap:8, marginBottom:36 }}>
        {STEPS.map((_, i) => (
          <div key={i} style={{ width: i <= stepIndex ? 28 : 8, height:8, borderRadius:4, background: i <= stepIndex ? "linear-gradient(135deg,#FF6B35,#FFBE0B)" : "rgba(255,255,255,0.1)", transition:"all 0.3s" }} />
        ))}
      </div>

      {/* Card */}
      <div style={{ width:"100%", maxWidth:480, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:24, padding:"36px 32px" }}>
        {children}
        <button
          onClick={() => canNext && onNext()}
          disabled={!canNext}
          style={{ width:"100%", marginTop:24, padding:"15px", borderRadius:13, border:"none", background: canNext ? "linear-gradient(135deg,#FF6B35,#FFBE0B)" : "rgba(255,255,255,0.06)", color: canNext ? "white" : "rgba(255,255,255,0.2)", fontSize:15, fontWeight:700, cursor: canNext ? "pointer" : "not-allowed", fontFamily:"inherit" }}
        >
          {nextLabel}
        </button>
        <div style={{ textAlign:"center", marginTop:12, fontSize:11, color:"rgba(255,255,255,0.2)" }}>
          Step {stepIndex + 1} of {STEPS.length}
        </div>
      </div>
    </div>
  );
}

export default function Onboarding({ goDashboard }) {
  const [step,      setStep]      = useState("onboard-1");
  const [bizName,   setBizName]   = useState("");
  const [bizType,   setBizType]   = useState("");
  const [platforms, setPlatforms] = useState(["google"]);
  const [incentive, setIncentive] = useState(true);

  const toggleP = (id) => setPlatforms(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const next = (s) => setStep(s);

  // ── Step 1: Business info ──
  if (step === "onboard-1") return (
    <Shell step="onboard-1" canNext={bizName.length > 1 && bizType.length > 0} onNext={() => next("onboard-2")}>
      <div style={{ fontSize:11, color:"#FF6B35", fontWeight:700, letterSpacing:"0.1em", marginBottom:10 }}>LET'S GET STARTED</div>
      <h2 style={{ fontSize:26, fontWeight:900, letterSpacing:"-0.03em", margin:"0 0 6px" }}>Tell us about your business</h2>
      <p style={{ fontSize:14, color:"rgba(255,255,255,0.4)", margin:"0 0 28px", lineHeight:1.6 }}>
        We'll pre-fill this on every customer review so they don't have to type a thing.
      </p>
      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:12, color:"rgba(255,255,255,0.4)", display:"block", marginBottom:7 }}>Business Name</label>
        <input
          value={bizName} onChange={e => setBizName(e.target.value)}
          placeholder="e.g. The Rustic Table"
          style={{ width:"100%", padding:"13px 16px", background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:12, color:"#fff", fontSize:15, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
        />
      </div>
      <div>
        <label style={{ fontSize:12, color:"rgba(255,255,255,0.4)", display:"block", marginBottom:10 }}>Business Type</label>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {BIZ_TYPES.map(t => (
            <button key={t} onClick={() => setBizType(t)} style={{ padding:"8px 14px", borderRadius:10, border:`1.5px solid ${bizType===t ? "#FF6B35":"rgba(255,255,255,0.1)"}`, background: bizType===t ? "rgba(255,107,53,0.15)":"transparent", color: bizType===t ? "#FF6B35":"rgba(255,255,255,0.45)", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
              {t}
            </button>
          ))}
        </div>
      </div>
    </Shell>
  );

  // ── Step 2: Platforms ──
  if (step === "onboard-2") return (
    <Shell step="onboard-2" canNext={platforms.length > 0} onNext={() => next("onboard-3")}>
      <div style={{ fontSize:11, color:"#FF6B35", fontWeight:700, letterSpacing:"0.1em", marginBottom:10 }}>PLATFORMS</div>
      <h2 style={{ fontSize:26, fontWeight:900, letterSpacing:"-0.03em", margin:"0 0 6px" }}>Where should reviews go?</h2>
      <p style={{ fontSize:14, color:"rgba(255,255,255,0.4)", margin:"0 0 28px", lineHeight:1.6 }}>
        Select all the platforms you want customer reviews posted to automatically.
      </p>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {PLATFORMS_LIST.map(p => (
          <div key={p.id} onClick={() => toggleP(p.id)} style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", borderRadius:13, border:`1.5px solid ${platforms.includes(p.id) ? p.color+"66":"rgba(255,255,255,0.08)"}`, background: platforms.includes(p.id) ? `${p.color}12`:"rgba(255,255,255,0.02)", cursor:"pointer", transition:"all 0.2s" }}>
            <div style={{ width:38, height:38, borderRadius:10, background:p.color, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontWeight:900, fontSize:16, flexShrink:0 }}>{p.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:700 }}>{p.name}</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{p.note}</div>
            </div>
            <div style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${platforms.includes(p.id) ? p.color:"rgba(255,255,255,0.2)"}`, background: platforms.includes(p.id) ? p.color:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"white", flexShrink:0 }}>
              {platforms.includes(p.id) && "✓"}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );

  // ── Step 3: Incentive ──
  if (step === "onboard-3") return (
    <Shell step="onboard-3" onNext={() => next("onboard-4")}>
      <div style={{ fontSize:11, color:"#FF6B35", fontWeight:700, letterSpacing:"0.1em", marginBottom:10 }}>INCENTIVES</div>
      <h2 style={{ fontSize:26, fontWeight:900, letterSpacing:"-0.03em", margin:"0 0 6px" }}>Reward your reviewers?</h2>
      <p style={{ fontSize:14, color:"rgba(255,255,255,0.4)", margin:"0 0 28px", lineHeight:1.6 }}>
        After a customer submits a review, you can automatically send them a thank-you discount. Businesses using incentives get 3× more reviews.
      </p>
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
        {[true, false].map(opt => (
          <div key={String(opt)} onClick={() => setIncentive(opt)} style={{ padding:"16px 18px", borderRadius:13, border:`1.5px solid ${incentive===opt ? "#FF6B35":"rgba(255,255,255,0.08)"}`, background: incentive===opt ? "rgba(255,107,53,0.1)":"rgba(255,255,255,0.02)", cursor:"pointer", transition:"all 0.2s" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:24 }}>{opt ? "🎁" : "⏭️"}</span>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color: incentive===opt ? "#fff":"rgba(255,255,255,0.6)" }}>
                  {opt ? "Yes — send a thank-you discount" : "No incentive for now"}
                </div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginTop:2 }}>
                  {opt ? "10% off their next visit · code sent automatically" : "You can always add this later in Settings"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {incentive && (
        <div style={{ background:"rgba(255,190,11,0.08)", border:"1px solid rgba(255,190,11,0.2)", borderRadius:12, padding:"14px 16px" }}>
          <div style={{ fontSize:11, color:"#FFBE0B", fontWeight:700, marginBottom:4 }}>DISCOUNT CODE</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#FFBE0B", letterSpacing:"0.05em" }}>THANKYOU10</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:3 }}>Customise the code and amount in your dashboard</div>
        </div>
      )}
    </Shell>
  );

  // ── Step 4: Done! ──
  if (step === "onboard-4") return (
    <div style={{ minHeight:"100vh", background:"#0a0a0f", fontFamily:"'Plus Jakarta Sans', sans-serif", color:"#f0ede8", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center" }}>
      <div style={{ fontSize:70, marginBottom:24 }}>🎉</div>
      <div style={{ fontSize:11, color:"#FF6B35", fontWeight:700, letterSpacing:"0.1em", marginBottom:12 }}>YOU'RE ALL SET</div>
      <h1 style={{ fontSize:"clamp(28px,5vw,48px)", fontWeight:900, letterSpacing:"-0.04em", margin:"0 0 14px" }}>
        Welcome to JiffyReview{bizName ? `, ${bizName}` : ""}!
      </h1>
      <p style={{ fontSize:16, color:"rgba(255,255,255,0.4)", maxWidth:420, lineHeight:1.7, fontWeight:300, marginBottom:40 }}>
        Your QR code is ready. Print it, place it on your receipts, and start collecting reviews today.
      </p>
      <div style={{ background:"white", display:"inline-block", padding:16, borderRadius:20, marginBottom:20, boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
        <MiniQR size={140} />
      </div>
      <div style={{ fontSize:12, color:"rgba(255,255,255,0.3)", marginBottom:28 }}>
        jiffyreview.com/r/{bizName.toLowerCase().replace(/\s+/g,"-") || "your-business"}
      </div>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", justifyContent:"center", marginBottom:52 }}>
        <button style={{ padding:"14px 28px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#FF6B35,#FFBE0B)", color:"white", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
          ↓ Download QR Code
        </button>
        <button onClick={goDashboard} style={{ padding:"14px 28px", borderRadius:12, border:"1.5px solid rgba(255,255,255,0.12)", background:"transparent", color:"rgba(255,255,255,0.5)", fontSize:15, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
          Go to Dashboard →
        </button>
      </div>
      <div style={{ display:"flex", gap:32, justifyContent:"center", flexWrap:"wrap" }}>
        {[["📊","Live dashboard","Track every review in one place"],["🔔","Instant alerts","Know the moment a review lands"],["🔒","Review gating","Bad reviews go to you first"]].map(([icon, title, desc]) => (
          <div key={title} style={{ maxWidth:160, textAlign:"center" }}>
            <div style={{ fontSize:28, marginBottom:8 }}>{icon}</div>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>{title}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", lineHeight:1.5 }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
